import { deleteTechnician, listOfTechnician } from "Api/Technician";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import DeleteModal from "Components/Base/DeleteModal";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import {
  ActionKey,
  ActionLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
} from "Components/constants/Role";
import {
  ContactNoKey,
  EmailKey,
  EmailLabel,
  PhoneNumberLabel,
  StaffLabel,
} from "Components/constants/Staff";
import {
  EXPERTISE_LABEL,
  TECHNICIAN_DRAFT_ID,
  TECHNICIAN_ID_LABEL,
  TECHNICIAN_LABEL,
  TECHNICIAN_NAME,
  TECHNICIAN_NAME_ACCESS_KEY,
} from "Components/constants/Technician";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Technician = () => {
  document.title = TECHNICIAN_LABEL;
  const navigate = useNavigate();
  const [technicianData, setTechnicianData] = useState<Array<any>>([]);
  const [technicianSearchData, setTechnicianSearchData] = useState<Array<any>>(
    []
  );
  const [loader, setLoader] = useState<boolean>(false);
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number>();
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleDelete = (id: string) => {
    setDeleteOpen(!deleteOpen);
    setDeleteId(Number(id));
  };

  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const technicianColumns = useMemo(
    () => [
      {
        header: TECHNICIAN_ID_LABEL,
        accessorKey: TECHNICIAN_DRAFT_ID,
        cell: (cell: any) => cell.getValue(),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: TECHNICIAN_NAME,
        accessorKey: TECHNICIAN_NAME_ACCESS_KEY,
        enableColumnFilter: false,
      },
      {
        header: EXPERTISE_LABEL,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div
            className={
              cell?.row?.original?.technicianExpertise?.length > 3
                ? `d-flex justify-content-start flex-wrap gap-2 icon tooltip-container-data`
                : `d-flex justify-content-start flex-wrap gap-2 icon`
            }
          >
            {cell?.row?.original?.technicianExpertise?.length > 3
              ? cell?.row?.original?.technicianExpertise
                  .slice(0, 3)
                  .map((item: any, index: number) => {
                    return (
                      <>
                        <span className="badge bg-light text-dark">
                          {item.expertise.expertise_name}
                        </span>
                        {index ===
                          cell?.row?.original?.technicianExpertise?.slice(0, 3)
                            .length -
                            1 && (
                          <span className="badge bg-light text-dark">
                            +
                            {
                              cell?.row?.original?.technicianExpertise.slice(3)
                                .length
                            }
                          </span>
                        )}
                      </>
                    );
                  })
              : cell?.row?.original?.technicianExpertise.map((item: any) => {
                  return (
                    <span className="badge bg-light text-dark">
                      {item.expertise.expertise_name}
                    </span>
                  );
                })}
            <span className="tooltip-text">
              {cell?.row?.original?.technicianExpertise.map(
                (item: any, index: number) => {
                  return index ===
                    cell?.row?.original?.technicianExpertise.length - 1
                    ? item.expertise.expertise_name
                    : `${item.expertise.expertise_name}, `;
                }
              )}
            </span>
          </div>
        ),
      },
      {
        header: PhoneNumberLabel,
        accessorKey: ContactNoKey,
        enableColumnFilter: false,
      },
      {
        header: EmailLabel,
        accessorKey: EmailKey,
        enableColumnFilter: false,
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => (
          <div className="d-flex justify-content-start enable-action flex-wrap gap-2 icon">
            <span className="tooltip-container">
              <i
                className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"
                onClick={() =>
                  navigate(`/edit-technician/${cell?.row?.original?.id}`)
                }
              ></i>
              <span className="tooltip-text">{editLabel}</span>
            </span>
            {/* delete future purpose */}
            {/* <span className="tooltip-container">
              <i
                className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                onClick={() => handleDelete(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{DeleteLabel}</span>
            </span> */}
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  const deleteTechnicianData = () => {
    setBtnLoader(true);
    deleteTechnician(Number(deleteId))
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res?.message);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => {
        setBtnLoader(false);
        setDeleteOpen(!deleteOpen);
        fetchTechnicianData();
      });
  };

  const fetchTechnicianData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      limit: customPageSize,
    };
    listOfTechnician(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setTechnicianData(res?.data?.positions);
          setTotalRecords(res?.data?.totalItems);
          setTotalPages(res?.data?.totalPages);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          if (searchValue) {
            fetchSearchTechnicianData();
          }
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchSearchTechnicianData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      type_of_user: StaffLabel,
      search: searchValue,
      limit: customPageSize,
    };
    listOfTechnician(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setTechnicianSearchData(response.data.positions);
          setTotalRecords(response.data.totalItems);
          setTotalPages(response?.data?.totalPages);
          setTotalNumberOfRows(response?.data?.numberOfRows);
        }
      })
      .catch((error) => {
        setTechnicianSearchData([]);
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue === "") {
      setCurrentPage(1);
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchValue) {
      fetchSearchTechnicianData();
    } else {
      fetchTechnicianData();
    }
  }, [currentPage, customPageSize, sortOrder, columnName, searchValue]);

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{TECHNICIAN_LABEL}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/add-technician")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>
        <div className="card py-2 px-4 overflow-x-auto">
          {loader || technicianData.length > 0 ? (
            <TableContainer
              customPageSize={customPageSize}
              setCustomPageSize={setCustomPageSize}
              isGlobalFilter={true}
              columns={technicianColumns}
              data={searchValue ? technicianSearchData : technicianData || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
        <DeleteModal
          btnLoader={Boolean(btnLoader)}
          onCloseClick={() => setDeleteOpen(!deleteOpen)}
          onDeleteClick={() => deleteTechnicianData()}
          show={deleteOpen}
        />
      </div>
    </div>
  );
};

export default Technician;
